import { useGetAuditLogsQuery } from '@/app/features/control-panel/dashboard/dashboard.api';
import CustomTable from '@/components/CustomTable';
import useAuth from '@/hooks/useAuth';
import useFilters from '@/hooks/useFilters';
import type { TableProps } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useMemo } from 'react';

interface Props {
  dates: [Dayjs | null, Dayjs | null];
}

export default function Activity({ dates }: Props) {
  const { organizationId } = useAuth();

  // Use filters hook
  const { page, limit, handlePagination } = useFilters();

  // Call audit logs API
  const {
    data: auditLogsData,
    isLoading: isLoadingAuditLog,
    isFetching,
  } = useGetAuditLogsQuery(
    {
      organization_id: organizationId,
      page: page || 1,
      limit: limit || 10,
      startDate: dates[0]?.format('YYYY-MM-DD'),
      endDate: dates[1]?.format('YYYY-MM-DD'),
    },
    { skip: !organizationId },
  );

  // Map audit logs data to table format
  const dataSource = useMemo(() => {
    if (!auditLogsData?.data?.data) {
      return [];
    }
    const logs = auditLogsData.data.data;
    return logs.map((log: any) => ({
      key: log.id,
      adminUser: log.action_by_name || 'N/A',
      action: log.log_message || 'N/A',
      module: log.module_name || 'N/A',
      ip_address: log?.ip_address || 'N/A',
      dateTime: log.timestamp
        ? dayjs(log.timestamp).format('DD/MM/YYYY, hh:mm A')
        : 'N/A',
    }));
  }, [auditLogsData]);

  const total = auditLogsData?.data?.pagination?.total || 0;

  const columns: TableProps<any>['columns'] = [
    {
      title: 'Admin User',
      dataIndex: 'adminUser',
      // align: "center",
      render: (val: string) => (
        <span className="lg:text-nowrap">{val ?? 'n/a'}</span>
      ),
    },

    {
      title: 'Action',
      dataIndex: 'action',
      render: (val) => val ?? 'n/a',
    },
    {
      width: 300,
      title: 'Module',
      dataIndex: 'module',
      render: (val) => val ?? 'n/a',
    },
    {
      width: 200,
      title: 'IP Address',
      dataIndex: 'ip_address',
      align: 'center',
      render: (val) => val ?? 'n/a',
    },
    {
      width: 200,
      title: 'Date & Time',
      dataIndex: 'dateTime',
      align: 'center',
      render: (val) => val ?? 'n/a',
    },
  ];

  return (
    <CustomTable
      page={page}
      limit={limit}
      hideRowSelect
      columns={columns}
      dataSource={dataSource}
      tableRootClassName="border-0 p-0"
      onPaginate={handlePagination}
      customTitle={<span className="text-[25px]">Recent Admin Activity</span>}
      loading={isLoadingAuditLog || isFetching}
      total={total}
    />
  );
}
